import { useState, useEffect } from 'react'
import {
  datasetFilesService,
  TablePageRecordsResponse,
} from '@/api/services/datasetFiles.service'

interface DatasetPreviewTableProps {
  datasetFileId: string
  datasetId: string
  knowledgeBaseId: string
  workspaceId: string
}

const DEFAULT_PAGE_SIZE = 10

const DatasetPreviewTable: React.FC<DatasetPreviewTableProps> = ({
  datasetFileId,
  datasetId,
  knowledgeBaseId,
  workspaceId,
}) => {
  // 表格数据
  const [tableData, setTableData] = useState<TablePageRecordsResponse>()
  // 加载状态
  const [loading, setLoading] = useState(false)
  // 当前页码
  const [currentPage, _] = useState(1)

  // 监听参数变化，加载表格数据
  useEffect(() => {
    if (!datasetFileId || !datasetId || !knowledgeBaseId || !workspaceId) {
      return
    }

    const fetchData = async () => {
      setLoading(true)
      try {
        const response = await datasetFilesService.getTablePageRecords({
          datasetFileId: parseInt(datasetFileId),
          datasetId: parseInt(datasetId),
          knowledgeBaseId: parseInt(knowledgeBaseId),
          workspaceId: parseInt(workspaceId),
          pageNo: currentPage,
          pageSize: DEFAULT_PAGE_SIZE,
        })
        console.log('DatasetPreviewTable response', response)
        setTableData(response)
      } catch (error) {
        console.error('加载表格数据失败:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [datasetFileId, datasetId, knowledgeBaseId, workspaceId, currentPage])

  // 如果没有数据或正在加载，显示相应状态
  if (loading) {
    return (
      <div className="space-y-2">
        <div className="flex items-center">
          <h3 className="text-base font-medium text-[#1D2129]">加载中...</h3>
        </div>
        <div className="bg-white rounded-lg p-8 text-center text-gray-500">
          <div className="text-lg mb-2">⏳</div>
          <div>正在加载表格数据...</div>
        </div>
      </div>
    )
  }

  if (!tableData?.records?.length) {
    return (
      <div className="space-y-2">
        <div className="flex items-center">
          <h3 className="text-base font-medium text-[#1D2129]">表格预览</h3>
        </div>
        <div className="bg-white rounded-lg p-8 text-center text-gray-500">
          <div className="text-lg mb-2">📊</div>
          <div>暂无数据</div>
        </div>
      </div>
    )
  }

  // 获取表头（从第一行数据中提取）
  const headers = tableData.tableColumns
    ? tableData.tableColumns
        .sort((a, b) => a.columnNo - b.columnNo)
        .map((col) => col.columnName)
    : Object.keys(tableData.records[0] || {})

  return (
    <div className="space-y-2">
      {/* 表名称 */}
      <div className="flex items-center">
        <h3
          className="text-base font-medium"
          style={{
            color: '#1D2129',
            fontSize: '16px',
          }}
        >
          表格预览
        </h3>
      </div>

      {/* 表格容器 */}
      <div
        className="overflow-hidden rounded-lg"
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
        }}
      >
        {/* 表格 */}
        <table className="w-full">
          {/* 表头 */}
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th
                  key={index}
                  className="px-4 py-3 text-center font-medium"
                  style={{
                    color: '#1D2129',
                    fontSize: '14px',
                    borderBottom: '1px solid #E9DFFF',
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>

          {/* 表格内容 */}
          <tbody>
            {tableData.records.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {headers.map((header, cellIndex) => {
                  const value = row[header]
                  // 根据列类型格式化显示
                  let displayValue = value
                  if (tableData.tableColumns) {
                    const column = tableData.tableColumns.find(
                      (col) => col.columnName === header
                    )
                    if (
                      column?.columnType === 'Number' &&
                      typeof value === 'number'
                    ) {
                      displayValue = value.toLocaleString()
                    }
                  }

                  return (
                    <td
                      key={cellIndex}
                      className="px-4 py-3 text-center"
                      style={{
                        color: '#1D2129',
                        fontSize: '14px',
                      }}
                    >
                      {displayValue}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 分页信息 */}
      {tableData.total && (
        <div className="text-sm text-gray-500 text-center mt-2">
          共 {tableData.total} 条记录，当前显示第 {tableData.current} 页
        </div>
      )}
    </div>
  )
}

export default DatasetPreviewTable
